import queryString from 'query-string'

const stringStardize = (str: string) => str?.trim().toLowerCase() ?? ''

class QueryParamBase {
  private getValues() {
    // return queryString.parse(decodeURIComponent(window.location.search))
    return queryString.parse(decodeURIComponent(window.location.search))
  }

  private getValuesMap() {
    const query = this.getValues()
    return new Map(Object.keys(query).map((x) => [stringStardize(x), query[x] as string]))
  }

  private getkeysMap() {
    const query = this.getValues()
    return new Map(Object.keys(query).map((x) => [stringStardize(x), x]))
  }

  private getPath = () => (window.location.pathname === '/' ? '' : window.location.pathname)

  // stringify<TParam extends {} = any>(param: TParam) {
  //   return `?${encodeURIComponent(queryString.stringify(param, { skipNull: true, skipEmptyString: true }))}`
  // }
  stringify = <TParam extends Record<string, any> = any>(param: TParam): string => {
    const entries = Object.keys(param)
      .filter((key) => param[key]) // skip null & empty string
      .reduce((a, key) => {
        const encodedValue = encodeURIComponent(param[key])
        a[key] = encodedValue
        return a
      }, {} as any)

    return '?' + queryString.stringify(entries, { skipNull: true, skipEmptyString: true })
  }

  replace<TParam extends {} = any, TState = any>(param: TParam, state?: TState) {
    window.history.replaceState(state ?? {}, '', `${this.getPath()}${this.stringify(param)}`)
    window.dispatchEvent(new window.PopStateEvent('popstate'))
  }

  patch<TParam extends { [key: string]: any } = any, TState = any>(param: TParam, state?: TState) {
    const query = this.getValues()
    const keyMap = this.getkeysMap()
    Object.keys(param).forEach((k) => {
      const key = stringStardize(k)
      if (keyMap.has(key)) {
        query[keyMap.get(key) ?? ''] = param[k]
      } else {
        query[k] = param[k]
      }
    })
    this.put(query, state)
  }

  replacePatch<TParam extends { [key: string]: any } = any, TState = any>(param: TParam, state?: TState) {
    const query = this.getValues()
    const keyMap = this.getkeysMap()
    Object.keys(param).forEach((k) => {
      const key = stringStardize(k)
      if (keyMap.has(key)) {
        query[keyMap.get(key) ?? ''] = param[k]
      } else {
        query[k] = param[k]
      }
    })
    this.replace(query, state)
  }

  put<TParam extends {} = any, TState = any>(param: TParam, state?: TState) {
    window.history.pushState(state ?? {}, '', `${this.getPath()}${this.stringify(param)}`)
    window.dispatchEvent(new window.PopStateEvent('popstate'))
  }

  deletes<TValue extends {} = any>(...keys: (keyof TValue)[]) {
    this.deletesWithState(keys)
  }

  replaceDeletes<TValue extends {} = any>(...keys: (keyof TValue)[]) {
    this.replaceDeletesWithState(keys)
  }

  replaceDeletesWithState<TValue extends {} = any, TState = any>(keys: (keyof TValue)[], state?: TState) {
    const query = this.getValues()
    const keyMap = this.getkeysMap()
    keys.forEach((k) => {
      const key = stringStardize(k.toString())
      if (keyMap.has(key)) {
        delete query[keyMap.get(key) ?? '']
      }
    })
    this.replace(query, state)
  }

  deletesWithState<TValue extends {} = any, TState = any>(keys: (keyof TValue)[], state?: TState) {
    const query = this.getValues()
    const keyMap = this.getkeysMap()
    keys.forEach((k) => {
      const key = stringStardize(k.toString())
      if (keyMap.has(key)) {
        delete query[keyMap.get(key) ?? '']
      }
    })
    this.put(query, state)
  }

  deleteAll() {
    window.history.replaceState({}, '', `${this.getPath()}`)
  }

  getAll = () => {
    return this.getValues()
  }

  gets<TValue extends {} = any>(...keys: (keyof TValue)[]) {
    const query = this.getValuesMap()
    return keys.reduce<Partial<TValue>>((a, k) => {
      const key = stringStardize(k.toString())
      if (query.has(key)) {
        a[k] = query.get(key) as any
      }
      return a
    }, {})
  }
}

export const QueryParam = new QueryParamBase()
// ;(window as any).QueryParam = QueryParam
export default QueryParam
